import { formatAppDate } from '../utility/dateLocale';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Group,
  Text,
  ActionIcon,
  Card,
  Image,
} from '@mantine/core';
import {
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { FileText, Dots } from 'tabler-icons-react';
import '../pages/KanbanColumn.css';
import { parsePhotoUrl } from '../utility/parsers';
import {Stage, Opportunity, stageColors, stageIcons } from './OpportunityPageTypes';
import { calculateTotalDuration, calculateStageDuration } from './OpportunityPageUtilities';

/* ───────────────────────────────
   COMPONENTE OpportunityCard
──────────────────────────────── */

export interface OpportunityCardProps {
    opportunity: Opportunity;
    record: Stage;
    color: string;
    icon: React.ReactNode;
    style?: React.CSSProperties;
    attributes?: any;
    listeners?: any;
    onClick?: (opportunity: Opportunity) => void;
  }
  
export const OpportunityCard = React.forwardRef<HTMLDivElement, OpportunityCardProps>(
    (
      { opportunity, record, color, icon, style, attributes, listeners, onClick },
      ref
    ) => {
      const { t } = useTranslation();

      function handleSettingsClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.stopPropagation();
        e.preventDefault();
        onClick && onClick(opportunity) 
        // Removido o estado settingsModalOpen, pois não é utilizado
      }
  
      return (
        <>
          <Card
            ref={ref}
            shadow="sm"
            radius="sm"
            p="xs"
            style={{
              width: 260,
              height: 210,
              margin: 'auto',
              borderLeft: `6px solid ${color}`,
              backgroundColor: '#fff',
              ...style
            }}
            onClick={onClick}
            {...attributes}
            {...listeners}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <div style={{ color, marginRight: 6, display: 'flex', alignItems: 'center' }}>
                  {icon}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', textAlign: 'left', minWidth: 0 }}>
                  <Text
                    weight={700}
                    size="xs"
                    style={{
                      fontSize: '10px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {opportunity.id.length > 20
                    ? 'ID: ' + opportunity.id.slice(0, 20) + '...'
                    : 'ID: ' + opportunity.id}
                  </Text>
                  <Text
                    weight={600}
                    size="xs"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {opportunity.name}
                  </Text>
                </div>
              </div>
              <ActionIcon
                variant="light"
                onPointerDown={(e: React.PointerEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={handleSettingsClick}
                style={{ backgroundColor: color, color: '#fff', zIndex: 0 }}
              >
                <Dots size={16} />
              </ActionIcon>
            </div>
  
            <Group spacing="xs" align="center" mt="xs">
              {opportunity.client_image && (
                <Image
                  src={parsePhotoUrl(opportunity.client_image)}
                  alt={opportunity.client}
                  width={32}
                  height={32}
                  radius="xl"
                />
              )}
              <Text
                  weight={700}
                  size="md"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
              >
                {opportunity.client.length > 20
                ? opportunity.client.slice(0, 20) + '...'
                : opportunity.client}
              </Text>
            </Group>
  
            <div style={{ marginTop: 8 }}>
              <Group position="apart" spacing="xs">
                <Text size="xs" color="dimmed">
                  {t('opportunity.card.value')}
                </Text>
                <Text size="xs">$ {opportunity.value}</Text>
              </Group>
              <Group position="apart" spacing="xs">
                <Text size="xs" color="dimmed">
                  {t('opportunity.card.prob')}
                </Text>
                <Text size="xs">{opportunity.probability}%</Text>
              </Group>
              <Group position="apart" spacing="xs">
                <Text size="xs" color="dimmed">
                  {t('opportunity.card.started')}
                </Text>
                <Text size="xs">{formatAppDate(record.created_at)}</Text>
              </Group>
              <Group position="apart" spacing="xs">
                <Text size="xs" color="dimmed">
                  {t('opportunity.card.timeSpent')}
                </Text>
                <Text size="xs">{t('opportunity.card.daysShort', { count: calculateStageDuration(record) })}</Text>
              </Group>
              <Group position="apart" spacing="xs">
                <Text size="xs" color="dimmed">
                  {t('opportunity.card.totalTime')}
                </Text>
                <Text size="xs">{t('opportunity.card.days', { count: calculateTotalDuration(opportunity.stages) })}</Text>
              </Group>
            </div>
          </Card>
        </>
      );
    }
  );
  OpportunityCard.displayName = 'OpportunityCard';
  
  /* ───────────────────────────────
     WRAPPER PARA DRAGGABLE
  ──────────────────────────────── */
  
export  function DraggableOpportunityCard({ opportunity, onClick }: { opportunity: Opportunity; onClick: () => void; }) {
    const currentStage = opportunity.stages.filter(st => st.finished_at === null)[0] || opportunity.stages[0];
    const draggableId = `${opportunity.id}/${currentStage.id}`;
    const { setNodeRef, attributes, listeners, transform } = useDraggable({ id: draggableId });
    const style = { transform: CSS.Translate.toString(transform), marginBottom: '8px' };
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <OpportunityCard
          opportunity={opportunity}
          record={currentStage}
          color={stageColors[currentStage.name] || '#000'}
          icon={stageIcons[currentStage.name] || <FileText size={18} />}
          onClick={onClick}
        />
      </div>
    );
  }